import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AddLockComponent } from 'src/app/building-managment/building-unit-details/add-lock/add-lock.component';
import { IDevice } from 'src/app/building-managment/building-unit-details/add-lock/lock-management-interfaces';
import { ViewAlertDetailsComponent } from 'src/app/building-managment/building-unit-details/view-alert-details/view-alert-details.component';
import { UpdateFirmwareDetailsComponent } from 'src/app/building-managment/building-unit-details/update-firmware-details/update-firmware-details.component';
import { DeviceSettingsComponent } from 'src/app/building-managment/device-settings/device-settings.component';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { Utility } from 'src/app/shared/utils/utility.service';

@Component({
  selector: 'app-shared-lock-devices-list',
  templateUrl: './shared-lock-devices-list.component.html',
  styleUrls: ['./shared-lock-devices-list.component.scss'],
})
export class SharedLockDevicesListComponent implements OnInit {
  @Input() deviceList!: Observable<IDevice[] | undefined>;
  readonly CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;

  constructor(
    private helperService: HelperService,
    private translate: TranslateService
  ) {}

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnInit(): void {}

  /**
   * @description: #getBatteryError function recieves battery percentage and activavtionstate and return the appropriate error message.
   * @param { string } batteryPercentage
   * @param { string } state
   * @returns { string } battery error
   */
  getBatteryError(activationState: string, batteryPercentage: string): string {
    switch (true) {
      case activationState === CONSTANTS.PENDING_ACTIVATION:
        return this.translate.instant('LOCK_INSTALLED_PROCESS');
      case activationState === CONSTANTS.PENDING_DEACTIVATION:
        return this.translate.instant('LOCK_DEACTIVATED');
      default: {
        const messageKey = Utility.getBatteryMessageKey(batteryPercentage);
        return messageKey ? this.translate.instant(messageKey) : '';
      }
    }
  }

  /**
   * @description: #getBatteryPercentage function recieves battery percentage in string and converts it to number.
   * @param { string } batteryPercentage
   * @returns { number } battery
   */

  getBatteryPercentage(batteryPercentage: string): number {
    return Utility.getBatteryPercentage(batteryPercentage);
  }

  /**
   * @description: #isBatteryLow function tells whether the battery is in a low, very low or
   * critically low state, which drives the red icon, red percentage and the warning banner.
   * @param { string } batteryPercentage
   * @returns { boolean } isLow
   */
  isBatteryLow(batteryPercentage: string): boolean {
    return Utility.isBatteryLow(batteryPercentage);
  }

  /**
   * @description: #getLockImg function decides the Lock image on the basis of battery percentage
   * @param { string } activationState
   * @param { string } batteryPercentage
   * @returns { string } imageType
   */
  getLockImg(
    activationState: string,
    batteryPercentage: string,
    deviceStatus: any
  ) {
    let battery = this.getBatteryPercentage(batteryPercentage);
    let imageType = '';
    if (
      activationState === CONSTANTS.PENDING_ACTIVATION ||
      battery <= Number(CONSTANTS.BATTERY_ALARAM_PERCENTAGE) ||
      activationState === CONSTANTS.PENDING_DEACTIVATION
    ) {
      imageType = CONSTANTS.ICO_LOCK_CIRCLE_OUTLINE_RED;
    } else if (
      activationState === CONSTANTS.ACTIVATED &&
      deviceStatus === CONSTANTS.DEVICE_IS_LOCKED &&
      battery > Number(CONSTANTS.BATTERY_ALARAM_PERCENTAGE)
    ) {
      imageType = CONSTANTS.ICO_LOCK_ACTIVITY;
    } else if (
      activationState === CONSTANTS.ACTIVATED &&
      deviceStatus === CONSTANTS.DEVICE_IS_UNLOCKED &&
      battery > Number(CONSTANTS.BATTERY_ALARAM_PERCENTAGE)
    ) {
      imageType = CONSTANTS.ICO_UNLOCK_ACTIVITY;
    } else imageType = CONSTANTS.ICO_LOCK_ACTIVITY;
    return imageType;
  }

  /* @description: #getBatteryImg function decides the battery image on the basis of battery percentage
   * @param { string } batteryPercentage
   * @returns { string } imageType
   */
  getBatteryImg(batteryPercentage: string): string {
    return Utility.getBatteryIcon(batteryPercentage);
  }

  /**
   * @description: #navigateToDeviceInfo function opens the modal poup for Lock Settings.
   * @param { string } deviceId
   * @param { string } deviceName
   * @output { void }
   */

  navigateToDeviceInfo(
    deviceId: string,
    deviceName: string,
    batteryPercentage: string,
    activationState: string,
    entity: string
  ): void {
    this.helperService.openModalDialog(
      DeviceSettingsComponent,
      {
        deviceId,
        deviceName,
        parentType: entity,
        batteryPercentage: batteryPercentage,
        activationState: activationState,
      },
      [CONSTANTS.FIXED_SCREEN_MODAL, CONSTANTS.SPACE_ZERO]
    );
  }

  /**
   * @description: add new lock
   * @inut {none}
   * @output {void}
   */
  addLock(): void {
    const dialogInstance = this.helperService.openModalDialog(
      AddLockComponent,
      { enableBackOperation: false, defaultNavigation: CONSTANTS.BUILDING },
      [CONSTANTS.FIXED_SCREEN_MODAL, CONSTANTS.ADD_LOCK]
    );
  }

  onSeeDetails(title: string) {
    this.helperService.openModalDialog(
      ViewAlertDetailsComponent,
      {
        title,
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }

  onUpdateFirmwareDetails() {
    this.helperService.openModalDialog(
      UpdateFirmwareDetailsComponent,
      {},
      CONSTANTS.FIXED_SCREEN_MODAL
    );
  }
}
